import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Stethoscope, UserPlus, Droplet, CalendarDays, Send } from "lucide-react";
import { api, errMsg, fmtDateTime, todayISO } from "@/lib/api";
import { Field, StatCard } from "@/components/Field";

const Line = ({ label, value, testid }) => (
  <div className="flex justify-between border-b border-[#e2e8f0] py-2 text-sm last:border-0">
    <span className="text-slate-600">{label}</span><span className="font-bold" data-testid={testid}>{value}</span>
  </div>
);

export default function Report() {
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [r, setR] = useState(null);
  const [sending, setSending] = useState(false);
  const load = () => api.get("/reports/monthly", { params: { month } }).then((res) => setR(res.data));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reports/monthly/send", null, { params: { month } });
      toast[data.email_sent ? "success" : "warning"](data.email_sent ? "Report inviato via email" : "Email non inviata, riprova più tardi");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  const label = new Date(`${month}-01T00:00:00`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  return (
    <div className="fade-up space-y-8" data-testid="report-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-[#3d655b]">Riepilogo</div>
          <h1 className="page-title mt-1">Report di {label}</h1>
          <p className="mt-1 text-sm text-slate-500">Il report del mese precedente arriva in automatico via email il 1° di ogni mese.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Mese"><input type="month" className="field w-auto" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="report-month-input" /></Field>
          <button className="btn-outline" onClick={send} disabled={sending} data-testid="report-send-button"><Send className="h-4 w-4" /> {sending ? "Invio…" : "Invia via email"}</button>
        </div>
      </div>
      {r && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label="Visite" value={r.visits} icon={Stethoscope} testid="report-visits" dark />
            <StatCard label="Nuovi pazienti" value={r.new_patients} icon={UserPlus} testid="report-new-patients" />
            <StatCard label="Prelievi" value={r.draws_total} icon={Droplet} testid="report-draws" />
            <StatCard label="Appuntamenti" value={r.appointments} icon={CalendarDays} testid="report-appointments" />
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="card">
              <h3 className="mb-3 text-lg font-bold">Attività</h3>
              <Line label="Pazienti visitati" value={r.patients_visited} testid="report-patients-visited" />
              <Line label="Appuntamenti confermati" value={r.appointments_confirmed} testid="report-appt-confirmed" />
              <Line label="Appuntamenti disdetti" value={r.appointments_cancelled} testid="report-appt-cancelled" />
              <Line label="Prenotati online" value={r.appointments_online} testid="report-appt-online" />
              <Line label="Pazienti in archivio" value={r.total_patients} testid="report-total-patients" />
            </div>
            <div className="card">
              <h3 className="mb-3 text-lg font-bold">Prelievi per sede</h3>
              {r.draws_by_sede.length ? r.draws_by_sede.map((s) => <Line key={s.sede} label={s.sede} value={s.count} />) : <p className="text-sm text-slate-400">Nessun prelievo registrato</p>}
              <p className="mt-3 text-xs text-slate-500">{r.draws_days} giornate registrate</p>
            </div>
            <div className="card">
              <h3 className="mb-3 text-lg font-bold">Nuovi pazienti</h3>
              {r.new_patient_names.length ? (
                <ul className="space-y-1 text-sm" data-testid="report-new-patient-list">{r.new_patient_names.map((n) => <li key={n}>{n}</li>)}</ul>
              ) : <p className="text-sm text-slate-400">Nessun nuovo paziente</p>}
            </div>
          </div>
          {r.email_sent_at && <p className="text-xs text-slate-500" data-testid="report-email-sent-at">Ultimo invio email: {fmtDateTime(r.email_sent_at)}</p>}
        </>
      )}
    </div>
  );
}
